import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./assets/css/Globals.css";
import "./config/supabase.js";
import { useDiagnosticTest } from "@/hooks/useDiagnosticTest";
import CourseLayout from "./pages/dashboard/courses/[courseId]/layout";
import LessonPracticePage from "./pages/dashboard/courses/[courseId]/lessons/[lessonId]/practice";
import ComingSoonPage from "./pages/coming-soon";
import { Loader } from "./components/ui/loader";

// Lazy imports for all components/pages
const Mission = lazy(() => import("@/pages/home/Mission.jsx"));
const Team = lazy(() => import("@/pages/home/Team.jsx"));
const Partners = lazy(() => import("@/pages/home/Partners.jsx"));
const Contact = lazy(() => import("@/pages/home/ContactUs"));
const QuizContainer = lazy(() => import("@/pages/courses/QuizContainer.js"));
const CourseRedirect = lazy(() => import("@/pages/courses/CourseRedirect.js"));
const SentryErrorBoundary = lazy(() => import("./config/SentryErrorBoundary.js"));

const LessonWrapper = lazy(() => import("@/pages/lesson/LessonWrapper.js"));
const Dashboard = lazy(() => import("@/pages/dashboard/Index"));
const DashboardHome = lazy(() => import("@/pages/dashboard/home"));
const CoursesPage = lazy(() => import("@/pages/dashboard/courses"));
const CoursePage = lazy(
	() => import("@/pages/dashboard/courses/[courseId]/index")
);
const ProgressPage = lazy(() => import("@/pages/dashboard/progress"));
const AssignmentsPage = lazy(() => import("@/pages/dashboard/assignments"));
const AssignmentPage = lazy(
	() => import("@/pages/dashboard/assignments/[assignmentId]")
);
const AchievementsPage = lazy(() => import("@/pages/dashboard/achievements"));
const ProfilePage = lazy(() => import("@/pages/dashboard/profile"));
const SettingsPage = lazy(() => import("@/pages/dashboard/settings"));
const MainLayout = lazy(() => import("./components/MainLayout"));
const LessonPage = lazy(
	() =>
		import(
			"@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/index.jsx"
		)
);
const CourseNotesPage = lazy(
	() => import("@/pages/dashboard/courses/[courseId]/notes/index.jsx")
);
const CourseQuizPage = lazy(
	() => import("@/pages/dashboard/courses/[courseId]/quiz/index.jsx")
);
const LessonQuizzesPage = lazy(
	() =>
		import(
			"@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/quizzes/index.jsx"
		)
);

const LessonNotesPage = lazy(
	() =>
		import(
			"@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/notes/index.jsx"
		)
);
const CourseMaterialsPage = lazy(
	() => import("@/pages/dashboard/courses/[courseId]/materials/index.jsx")
);
const FlashcardsPage = lazy(
	() => import("@/pages/dashboard/courses/[courseId]/flashcards/index.jsx")
);
const SubscriptionPage = lazy(() => import("@/pages/subscription/index.jsx"));
const CheckoutPage = lazy(
	() => import("@/pages/subscription/checkout/[planId]/index.jsx")
);
const Home = lazy(() => import("@/pages/index.jsx"));
const LoginPage = lazy(() => import("@/pages/auth/login/index.jsx"));
const RegisterPage = lazy(() => import("@/pages/auth/register/index.jsx"));
const ForgotPasswordPage = lazy(
	() => import("@/pages/auth/forgot-password/index.jsx")
);
const ResetPasswordPage = lazy(
	() => import("@/pages/auth/reset-password/index.jsx")
);
// const SuccessPage = lazy(() => import("@/pages/auth/success/index.jsx"));
// const VerifyEmailInstructionsPage = lazy(() => import("@/pages/auth/verify-email/index.jsx"));
// const VerifyEmailPage = lazy(() => import("@/pages/auth/verify-email/[token]/index.jsx"));
const BenchmarkTest = lazy(() => import("./pages/misc/benchmark-test.jsx"));
const MockExamLobby = lazy(() => import("@/pages/mock-exam/index.jsx"));
const MockExamSession = lazy(() => import("@/pages/mock-exam/session.jsx"));
const MockExamResults = lazy(() => import("@/pages/mock-exam/results.jsx"));
const AdminLayout = lazy(() => import("@/components/admin/admin-layout.jsx"));
const AdminOverview = lazy(() => import("@/pages/admin/index.jsx"));
const AdminUsers = lazy(() => import("@/pages/admin/users.jsx"));
const AdminUserDetail = lazy(() => import("@/pages/admin/user-detail.jsx"));
const AdminActivity = lazy(() => import("@/pages/admin/activity.jsx"));
const AdminQuestions = lazy(() => import("@/pages/admin/questions.jsx"));
const AdminExams = lazy(() => import("@/pages/admin/exams.jsx"));
const AdminTaxonomy = lazy(() => import("@/pages/admin/taxonomy.jsx"));
const AdminContent = lazy(() => import("@/pages/admin/content.jsx"));
const AdminCourseDetail = lazy(() => import("@/pages/admin/course-detail.jsx"));
const AdminUnitDetail = lazy(() => import("@/pages/admin/unit-detail.jsx"));
const AdminLessonDetail = lazy(() => import("@/pages/admin/lesson-detail.jsx"));
const AdminRoles = lazy(() => import("@/pages/admin/roles.jsx"));
const AuthLayout = lazy(() => import("./components/AuthLayout.jsx"));
const NotFoundPage = lazy(() => import("./pages/not-found.jsx"));
const {
	AuthProvider,
	AuthRedirect,
	AuthRequired,
} = require("./contexts/useAuth.jsx");
const { ThemeProvider } = require("./components/ThemeContext");

Sentry.init({
	dsn: "https://7e5589f837c1848a7a7486d7bd8a64f8@o4508677879824384.ingest.us.sentry.io/4508677888016384",
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],
	tracesSampleRate: 0.2,
	replaysSessionSampleRate: 0.05,
	replaysOnErrorSampleRate: 0.2,
	environment: process.env.NODE_ENV,
	beforeSend(event) {
		if (process.env.NODE_ENV === "development") {
			return null;
		}
		return event;
	},
});

// Mounted for the whole app so a benchmark-test result graded while logged
// out gets saved as soon as the user authenticates, no matter which page
// the login/signup/OAuth redirect happens to land on.
function PendingDiagnosticResultSync() {
	const { savePendingResult } = useDiagnosticTest();

	useEffect(() => {
		savePendingResult();
	}, [savePendingResult]);

	return null;
}

function App() {
	return (
		<SentryErrorBoundary>
			<Router>
				<AuthProvider>
					<PendingDiagnosticResultSync />
					<ThemeProvider>
						<div className="app">
							<Suspense fallback={<Loader />}>
								<Routes>
									{/* Landing page  */}
									<Route element={<MainLayout />}>
										<Route
											path="/mission"
											element={<Mission />}
										/>
										<Route
											path="/team"
											element={<Team />}
										/>
										<Route
											path="/partners"
											element={<Partners />}
										/>
										<Route
											path="/contact"
											element={<Contact />}
										/>
												
										{/* <Route
											path="/courses"
											element={<Courses />}
										/> */}

										<Route
											path="/courses/:subjectSlug/:courseSlug/:unitSlug/:lessonSlug/:lessonId/quiz"
											element={<QuizContainer />}
										/>
										<Route
											path="/courses/:subjectSlug/:courseSlug"
											element={<CourseRedirect />}
										/>
										<Route
											path="/courses/:subjectSlug/:courseSlug/:unitSlug/:lessonSlug/:lessonId"
											element={<LessonWrapper />}
										/>

										<Route path="/" element={<Home />} />
										<Route
											path="/subscription"
											element={<SubscriptionPage />}
										/>
										<Route
											path="/subscription/checkout/:planId"
											element={<CheckoutPage />}
										/>
										<Route
											path="/benchmark-test"
											element={
												<AuthRequired>
													<BenchmarkTest />
												</AuthRequired>
											}
										/>
										<Route
											path="/mock-exam"
											element={<MockExamLobby />}
										/>
										<Route
											path="/mock-exam/results/:sessionId"
											element={<MockExamResults />}
										/>
									</Route>
									{/* The sitting itself renders the exam-hall CBT
									    chrome edge to edge, so it stays outside
									    MainLayout — a site header on screen would
									    break the illusion (and the real client has
									    nothing to navigate away to). */}
									<Route
										path="/mock-exam/session/:sessionId"
										element={<MockExamSession />}
									/>

									{/* Admin console. AdminLayout gates on the
									    caller's role, but that is only what gets
									    rendered — Supabase RLS and the Strapi
									    admin policy are what actually refuse a
									    non-admin's reads and writes. */}
									<Route path="/admin" element={<AdminLayout />}>
										<Route index element={<AdminOverview />} />
										<Route
											path="users"
											element={<AdminUsers />}
										/>
										<Route
											path="users/:userId"
											element={<AdminUserDetail />}
										/>
										<Route
											path="activity"
											element={<AdminActivity />}
										/>
										<Route
											path="questions"
											element={<AdminQuestions />}
										/>
										<Route
											path="exams"
											element={<AdminExams />}
										/>
										<Route
											path="taxonomy"
											element={<AdminTaxonomy />}
										/>
										<Route
											path="content"
											element={<AdminContent />}
										/>
										<Route
											path="content/courses/:courseId"
											element={<AdminCourseDetail />}
										/>
										<Route
											path="content/units/:unitId"
											element={<AdminUnitDetail />}
										/>
										<Route
											path="content/lessons/:lessonId"
											element={<AdminLessonDetail />}
										/>
										<Route
											path="roles"
											element={<AdminRoles />}
										/>
									</Route>
									<Route
										path="/coming-soon"
										element={<ComingSoonPage />}
									/>
									{/* Authentication Pages */}
									<Route
										element={
											<AuthRedirect>
												<AuthLayout />
											</AuthRedirect>
										}>
										<Route
											path="/auth/login"
											element={<LoginPage />}
										/>
										<Route
											path="/auth/register"
											element={<RegisterPage />}
										/>
										<Route
											path="/auth/forgot-password"
											element={<ForgotPasswordPage />}
										/>
										<Route
											path="/auth/reset-password"
											element={<ResetPasswordPage />}
										/>

										{/* <Route path="/auth/verify-email" element={<VerifyEmailInstructionsPage />} />
                                        <Route path="/auth/verify-email/:token" element={<VerifyEmailPage />} />
                                        <Route path="/auth/success" element={<SuccessPage />} /> */}
													 
									</Route>

									{/* Student Dashboard */}
									<Route
										path="/dashboard"
										element={
											<AuthRequired>
												<Dashboard />
											</AuthRequired>
										}>
										<Route
											path="/dashboard"
											element={<DashboardHome />}
										/>
										<Route
											path="/dashboard/profile"
											element={<ProfilePage />}
										/>
										<Route
											path="/dashboard/courses"
											element={<CoursesPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId"
											element={<CourseLayout />}>
											<Route
												index
												element={<CoursePage />}
											/>

											<Route
												path="lessons/:lessonId"
												element={<LessonPage />}
											/>
											<Route
												path="lessons/:lessonId/notes"
												element={<LessonNotesPage />}
											/>
											<Route
												path="lessons/:lessonId/quizzes"
												element={<LessonQuizzesPage />}
											/>
											<Route
												path="lessons/:lessonId/practice"
												element={<LessonPracticePage />}
											/>
											<Route
												path="materials"
												element={
													<CourseMaterialsPage />
												}
											/>
											<Route
												path="quiz"
												element={<CourseQuizPage />}
											/>
											<Route
												path="notes"
												element={<CourseNotesPage />}
											/>
											<Route
												path="flashcards"
												element={<FlashcardsPage />}
											/>
											<Route
												path="quiz/:lessonId"
												element={<LessonQuizzesPage />}
											/>
										</Route>
										<Route
											path="/dashboard/progress"
											element={<ProgressPage />}
										/>
										<Route
											path="/dashboard/assignments"
											element={<AssignmentsPage />}
										/>
										<Route
											path="/dashboard/assignments/:assignmentId"
											element={<AssignmentPage />}
										/>
										<Route
											path="/dashboard/achievements"
											element={<AchievementsPage />}
										/>
										<Route
											path="/dashboard/settings"
											element={<SettingsPage />}
										/>
									</Route>

									<Route
										path="*"
										element={<NotFoundPage />}
									/>
								</Routes>
							</Suspense>
							<ToastContainer />
						</div>
					</ThemeProvider>
				</AuthProvider>
			</Router>
		</SentryErrorBoundary>
	);
}

export default App;
